import { types, flow, getRoot, getParent } from 'mobx-state-tree'
import { getDefaultPaymentMethodToken, getDefaultPaymentMethodBin, getPaymentMethodBin } from "~/services/payments";
import Network from "~/stores/models/Network"


const BraintreeThreeDSecureStore = types
  .model('BraintreeThreeDSecureStore', {
    paymentMethodNonce: types.maybeNull(types.string),
    paymentMethodBin: types.maybeNull(types.string),
    paymentMethodNonceService: types.optional(Network, {}),
    verifiedNonce: types.maybeNull(types.string)
  })
  .views(self => ({
    get sessionToken() {
      const rootStore = getRoot(self)
      return rootStore.auth.user.sessionToken
    },
    get braintreeStore() {
      return getParent(self)
    },
    get braintreeClient() {
      return self.braintreeStore.client
    },
    get threeDSecureStore() {
      const paymentStore = getParent(self,2)
      return paymentStore.threeDSecure
    },
    get isLoading() {
      return self.paymentMethodNonceService.isLoading
    }
  }))
  // volatile are things that are not serialized or tracked by MST. The things I store here are object pointers to created payment instances. These pointers can not be serialized, so they are referenced here.
  // eslint-disable-next-line
  .volatile(self => ({
    threeDSecureClient: null,
  }))
  .actions(self => ({
    setPaymentMethodNonce(value) {
      self.paymentMethodNonce = value
    },
    setPaymentMethodBin(value) {
      self.paymentMethodBin = value
    },
    setVerifiedNonce(value) {
      self.verifiedNonce = value
    },
    setThreeDSecureClient(client) {
      self.threeDSecureClient = client
    },
    fetchTokenizedPaymentMethod: flow(function* fetchTokenizedPaymentMethodGen() {
      try {
        self.paymentMethodNonceService.requestReset()
        self.paymentMethodNonceService.requestStart()
        // Todo: We'd like to have the token & bin combined at some point to avoid the extra network requests
        let token
        let bin
        const paymentStore = getParent(self,2)
        const rootStore = getRoot(self)
        if (paymentStore.isPaymentMethodJustAdded) {
          token = yield getDefaultPaymentMethodToken(self.sessionToken, rootStore.auth.user.email)
          bin = yield getDefaultPaymentMethodBin(self.sessionToken, rootStore.auth.user.email)
        } else {
          token = self.braintreeStore.nonce
          if (!token) {
            token = yield getDefaultPaymentMethodToken(self.sessionToken, rootStore.auth.user.email)
          }
          bin = yield getDefaultPaymentMethodBin(self.sessionToken, rootStore.auth.user.email, token)
        }
        self.setPaymentMethodNonce(token)
        self.setPaymentMethodBin(bin)
        self.paymentMethodNonceService.requestComplete()
      } catch (error) {
        console.error("Error fetching tokenized info", error)
        self.paymentMethodNonceService.requestError(error)
        throw error
      }
    }),
    initialize3DSClient: flow(function* initialize3DSClientGen() {
      if (!self.threeDSecureClient) {
        const threeDClient = yield import('braintree-web/three-d-secure')
        const clientResponse = yield threeDClient.create({
          version: 2,
          client: self.braintreeClient
        })
        self.setThreeDSecureClient(clientResponse)
      }
    }),
    verifyCard: flow(function* verifyCardGen() {
      // https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3
      const response = yield self.threeDSecureClient.verifyCard({
        amount: self.threeDSecureStore.verify3DSAmount,
        nonce: self.paymentMethodNonce,
        bin: self.paymentMethodBin,
        addFrame: (err, iframe) => {
          if(err) {
            return console.error('Error adding 3DS frame ',err)
          }
          // Set up your UI and add the iframe.
          return self.threeDSecureStore.modalDOMref.appendChild(iframe);
        },
        removeFrame: () => {
            self.threeDSecureStore.modalDOMref.innerHTML = ""
        },
        onLookupComplete (data, next) {
          next();
        }
      })
      // If liabilityShifted is false, then the user failed 3D Secure authentication.
      if (response.liabilityShiftPossible && !response.liabilityShifted) {
        throw new Error("3DS Authentication Failed")
      }
      self.setVerifiedNonce(response.nonce)
    }),
    /**
     * This is the method payment stores three d secure store calls to perform 3DS
     */
    verify: flow(function* initGen() {
      yield self.fetchTokenizedPaymentMethod()
      yield self.initialize3DSClient()
      yield self.verifyCard()
      return { nonce: self.verifiedNonce }
    }),
    /**
     * This is the method payment stores three d secure store calls to reset 3DS
     */
    teardown() {
      self.setPaymentMethodNonce(null)
      self.setVerifiedNonce(null)
    }
  }))

export default BraintreeThreeDSecureStore
